import { useCallback, useEffect, useState } from 'react';
import { FileText, RefreshCw, ShoppingBag, Loader2, WifiOff, History, Building2 } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import portalApi from '../lib/portalApi';
import { usePortalAuth } from '../PortalAuthContext';
import { PortalEmptyState } from '../ui/PortalEmptyState';
import { errorText } from '../health/healthConfig';

const KIND_META = {
  prescription: { label: 'Prescription', icon: FileText, cls: 'bg-violet-100 text-violet-700' },
  refill: { label: 'Refill', icon: RefreshCw, cls: 'bg-amber-100 text-amber-700' },
  order: { label: 'Purchase', icon: ShoppingBag, cls: 'bg-emerald-100 text-emerald-700' },
};

export default function PortalHistoryPage() {
  const { patient, family, activeMemberId, activeMember, setActiveMember } = usePortalAuth();
  const [events, setEvents] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [pharmacyFilter, setPharmacyFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const response = await portalApi.get('/history');
      setEvents(response.data.events || []);
      setPharmacies(response.data.pharmacies || []);
    } catch (e) {
      setLoadError(errorText(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = events.filter((e) =>
    (kindFilter === 'all' || e.kind === kindFilter) &&
    (pharmacyFilter === 'all' || e.branch_name === pharmacyFilter));

  const who = activeMember ? activeMember.name : (patient?.name || 'you');

  return (
    <div className="space-y-5" data-testid="portal-history-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid="portal-history-title">History</h1>
        <p className="text-sm text-slate-500 mt-1">
          Every prescription, refill and purchase for {who}, across all the pharmacies you use.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="portal-history-filters">
        {family.length > 0 && (
          <Select value={activeMemberId || 'self'} onValueChange={(v) => setActiveMember(v === 'self' ? '' : v)}>
            <SelectTrigger className="w-[11rem]" data-testid="portal-history-person-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="self" data-testid="portal-history-person-self">{patient?.name || 'Me'}</SelectItem>
              {family.map((m) => (
                <SelectItem key={m.patient_user_id} value={m.patient_user_id}
                  data-testid={`portal-history-person-${m.patient_user_id}`}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="w-[10rem]" data-testid="portal-history-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="portal-history-kind-all">Everything</SelectItem>
            {Object.entries(KIND_META).map(([key, meta]) => (
              <SelectItem key={key} value={key} data-testid={`portal-history-kind-${key}`}>{meta.label}s</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {pharmacies.length > 1 && (
          <Select value={pharmacyFilter} onValueChange={setPharmacyFilter}>
            <SelectTrigger className="w-[13rem]" data-testid="portal-history-pharmacy-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="portal-history-pharmacy-all">All pharmacies</SelectItem>
              {pharmacies.map((name) => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400" data-testid="portal-history-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : loadError ? (
        <PortalEmptyState icon={WifiOff} title="Couldn't load your history" description={loadError}
          testId="portal-history-error"
          action={<Button variant="outline" size="sm" onClick={load} data-testid="portal-history-retry">Try again</Button>} />
      ) : shown.length === 0 ? (
        <PortalEmptyState icon={History} title="Nothing here yet"
          description="Prescriptions, refills and purchases from every pharmacy you use will appear on this timeline."
          testId="portal-history-empty" />
      ) : (
        <Card>
          <CardContent className="p-0 divide-y" data-testid="portal-history-list">
            {shown.map((e) => {
              const meta = KIND_META[e.kind] || KIND_META.order;
              return (
                <div key={`${e.kind}-${e.event_id}`} className="flex gap-3 p-4" data-testid={`portal-history-${e.event_id}`}>
                  <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${meta.cls}`}>
                    <meta.icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium min-w-0 [overflow-wrap:anywhere]">{e.title}</span>
                      <Badge variant="secondary" className="text-[11px]">{meta.label}</Badge>
                      <Badge variant="outline" className="text-[11px] capitalize">{e.status}</Badge>
                    </div>
                    {e.subtitle && <p className="text-xs text-slate-600 mt-1 [overflow-wrap:anywhere]">{e.subtitle}</p>}
                    <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-1">
                      <Building2 className="w-3 h-3" />{e.branch_name}
                      <span>· {new Date(e.at).toLocaleString()}</span>
                      {e.total != null && <span>· GHS {Number(e.total).toFixed(2)}</span>}
                    </p>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
